import React from "react";
import { useSearchParams } from "react-router";
import { CircularProgress, LongTextLabel } from "@sscs/ui";
import { BackToLogin, LoginContentWrapper } from "Components";
import styles from "./acceptInvite.module.scss";
import AcceptInviteForm from "./AcceptInviteForm";

type AcceptInviteContentProps = {
  isVerifyingInvite: boolean;
  hasInviteVerificationFailed: boolean;
  isAccountCreated: boolean;
  onAccept: () => void;
  validationError?: string;
};

const AcceptInviteContent = ({
  isVerifyingInvite,
  hasInviteVerificationFailed,
  isAccountCreated,
  onAccept,
  validationError,
}: AcceptInviteContentProps): React.JSX.Element => {
  const [searchParams] = useSearchParams();
  const inviterEmail = searchParams.get("inviter_email");
  const inviterOrganization = searchParams.get("inviter_organization");
  const inviteTitle = (
    <>
      <LongTextLabel label={inviterEmail ?? "--"} maxCharacters={60} inline />{" "}
      <span>has invited you to join</span>{" "}
      <LongTextLabel
        label={inviterOrganization ?? "--"}
        maxCharacters={60}
        inline
      />
    </>
  );
  const inviteByline = "To get started, setup your account";

  if (isVerifyingInvite) {
    return (
      <LoginContentWrapper title="Verifying invite">
        <CircularProgress size="large" />
      </LoginContentWrapper>
    );
  }

  if (hasInviteVerificationFailed) {
    return (
      <LoginContentWrapper title="Invite verification failed">
        {
          <span className={styles["expired-invite"]}>
            {validationError?.toLowerCase().includes("expired") ? (
              <>
                Your invitation has expired. To get a new one, ask{" "}
                <LongTextLabel
                  label={inviterEmail ?? "--"}
                  maxCharacters={60}
                  inline
                />{" "}
                to invite you again.
              </>
            ) : (
              validationError
            )}
          </span>
        }
      </LoginContentWrapper>
    );
  }

  if (isAccountCreated) {
    return (
      <LoginContentWrapper
        title="Account created"
        byline="You can now log in with your email and password."
      >
        <BackToLogin />
      </LoginContentWrapper>
    );
  }

  return (
    <LoginContentWrapper title={inviteTitle} byline={inviteByline}>
      <AcceptInviteForm onSuccess={onAccept} />
    </LoginContentWrapper>
  );
};

export default AcceptInviteContent;
